import { MODERATION_APPEALS_ENABLED, BROKEN_MEDIA_ALERTS_ENABLED } from '../constants/featureFlags';

/**
 * Enum for notification types in the application
 * Each type has a numeric value and a description
 */
export const NOTIFICATION_TYPES = {
  // Wheel-related notifications
  SHARE: {
    value: 1,
    description: 'Wheel share notification',
    icon: 'share'
  },
  CLONE: {
    value: 2,
    description: 'Wheel clone notification',
    icon: 'clone'
  },
  LIVE_CLONE: {
    value: 3,
    description: 'Live wheel clone notification',
    icon: 'liveclone'
  },
  FAVORITE: {
    value: 4,
    description: 'Wheel favorite/like notification',
    icon: 'heart'
  },
  UNLOCK: {
    value: 5,
    description: 'Wheel unlock notification',
    icon: 'unlock'
  },
  LOCK: {
    value: 6,
    description: 'Wheel lock notification',
    icon: 'lock'
  },
  FOLLOW: {
    value: 7,
    description: 'User follow notification',
    icon: 'follow'
  },

  // System notifications
  WARNING: {
    value: 8,
    description: 'System warning notification',
    icon: 'warning'
  },
  TIP: {
    value: 9,
    description: 'System tip notification',
    icon: 'bulb'
  },
  // NOTE (type-drift, 2026-07-25): the FE names below drifted from the backend's
  // authoritative NOTIFICAITON_TYPE choices (notifications/models.py). The VALUES
  // are the contract and are correct; only the FE labels are stale:
  //   10 -> BE "Expiration"  (FE labelled STORAGE_WARNING)
  //   11 -> BE "Payment"     (FE labelled AUTO_DEPOSIT)
  //   13 -> BE "Storage Capacity Full"    (FE RED_STORAGE)
  //   14 -> BE "Storage Capacity Warning" (FE YELLOW_STORAGE)
  // Left as-is to avoid churning the icon maps that key off these names; routing
  // below is keyed by value and maps each to its correct destination.
  STORAGE_WARNING: {
    value: 10,
    description: 'Storage warning notification',
    icon: 'warning'
  },
  AUTO_DEPOSIT: {
    value: 11,
    description: 'Auto deposit notification',
    icon: 'warning'
  },
  TAX: {
    value: 12,
    description: 'Tax-related notification',
    icon: 'tax'
  },
  RED_STORAGE: {
    value: 13,
    description: 'Critical storage notification',
    icon: 'red_storage'
  },
  YELLOW_STORAGE: {
    value: 14,
    description: 'Warning storage notification',
    icon: 'yellow_storage'
  },
  // Added 2026-07-25 to match the backend, which emits these live but the FE
  // never mapped them -> they fell through to the /settings default (data-loss
  // routing bug). Both are wheel-instance notifications (ref_id = wheel id).
  AI_WHEEL: {
    value: 15,
    description: 'AI wheel generation notification',
    icon: 'liveclone'
  },
  COMMENT: {
    value: 16,
    description: 'Wheel comment notification',
    icon: 'comment'
  },
  // notif-comment-anchor follow-up (2026-08-12): MEASURED LIVE (own headed
  // Playwright, deployed bundle) that a type-17 notification click was a silent
  // dead click — the FE enum never mapped it, so getNotificationRedirectUrl fell
  // through to `default: return null`. Confirmed via the BE's own
  // notifications/migrations/0009_alter_notifications_type.py: type 17 =
  // WHEEL_REACTION. G-161 verified before wiring: fetched the live REACTION row
  // (id 0ff0eb98…, ref_id "2636") — `instance_type: 1` and the SAME image/
  // instance_name as this account's confirmed wheel-instance COMMENT row for the
  // same wheel, i.e. `ref_id` is genuinely the WHEEL id here (not a segment or
  // reaction id) — same shape as CLONE/FAVORITE/UNLOCK, so it joins
  // WHEEL_INSTANCE_TYPES below rather than getting a special case.
  WHEEL_REACTION: {
    value: 17,
    description: 'Wheel reaction notification',
    icon: 'heart'
  },
  // ISP-9999 (60-D, 2026-08-19): 16/17 are already taken (COMMENT/WHEEL_REACTION,
  // both landed after the 60-D spec's original -- stale -- measurement) -- next
  // free BE value is 18. Matches notifications/models.py Notifications.
  // COLLABORATION_INVITE (notifications/migrations/0010_alter_notifications_type.py).
  COLLABORATION_INVITE: {
    value: 18,
    description: 'Wheel collaboration invite notification',
    icon: 'collab'
  },
  // ISP-9999 (collab-link-join-no-notification): the wheel OWNER's
  // notification that someone joined via their shareable collaboration link
  // (BE yuga_wheel/collaboration/service.py notify_owner_of_link_join).
  // Distinct from COLLABORATION_INVITE (18), which is addressed to the
  // INVITEE of an email invite, not the owner. Matches
  // notifications/models.py Notifications.COLLABORATION_JOINED
  // (notifications/migrations/0011_alter_notifications_type.py).
  COLLABORATION_JOINED: {
    value: 19,
    description: 'Wheel collaboration join notification',
    icon: 'collab'
  },
  // ISP-9999 (cm-0919b-owner-notification-when-content-is-blocked-or-pulled-
  // does-not-exist-build-dev-first): the wheel OWNER's notification that
  // their content was quarantined/removed by moderation. Matches
  // notifications/models.py Notifications.MODERATION_CONTENT_HIDDEN
  // (notifications/migrations/0012_alter_notifications_type.py). A system
  // notification (not an actor event), same shape as WARNING -- reuses the
  // warning badge/icon (no dedicated asset needed).
  MODERATION_CONTENT_HIDDEN: {
    value: 20,
    description: 'Content hidden by moderation notification',
    icon: 'warning'
  },
  // feat-0923-broken-media-alerts (SPEC.md contract): one per wheel (or
  // avatar), created on first break and again only when the count rises above
  // the count at the last notification (BE-owned cadence). payload
  // {wheel_id, count} -- wheel_id is present for a wheel's broken media,
  // ABSENT for a broken avatar. 19/20 are taken above (COLLABORATION_JOINED /
  // MODERATION_CONTENT_HIDDEN, both landed after this feature's spec was
  // drafted) -- next free value is 21; must match the BE's
  // notifications/models.py Notifications choice for this feature.
  // Deliberately NOT a WHEEL_INSTANCE_TYPES member below: every other
  // wheel-instance type routes to `/wheel/<id>` (the public view), but this
  // one must open the Edit Wheel builder's broken-media list
  // (`?brokenMedia=1`), so it gets its own case in getNotificationRedirectUrl.
  BROKEN_MEDIA: {
    value: 21,
    description: 'Broken media notification',
    icon: 'warning'
  },
  // stgfeat-1007-P0 (Gordon 2026-10-07 decision, option 1): a new DM/chat
  // message now reaches the Notifications feed too -- previously (ISP-9999
  // #3425, RN#556 push audit) type 22 was push-only
  // (messaging/services/push.py sent it with commit_notification=False), so
  // the FE never needed an entry for it. BE now flips that for every DM
  // (comment likes / chat-message reactions are unchanged, stay silent).
  // Matches notifications/models.py Notifications.DM_MESSAGE.
  DM_MESSAGE: {
    value: 22,
    description: 'New chat message notification',
    icon: 'message'
  },
  // kelly-1003-P2-teams-invite-no-in-app-notification: a workspace (Teams)
  // email invite, addressed to the invitee, for an address that already has
  // an iYuga account (BE workspaces/services/invites.py
  // notify_invitee_in_app). Matches notifications/models.py
  // Notifications.WORKSPACE_INVITE.
  WORKSPACE_INVITE: {
    value: 23,
    description: 'Workspace (team) invite notification',
    icon: 'collab'
  },
  // T28.7 (Data Entry Lock Phase 2) -- BE notifications/models.py 24 / 25.
  // extras: { submission_id, wheel_id, status, audience: 'owner' | 'filler' }.
  DATA_ENTRY_REVIEW_NEEDED: {
    value: 24,
    description: 'Data entry answers need your review',
    icon: 'lock'
  },
  DATA_ENTRY_RESULT: {
    value: 25,
    description: 'Data entry result',
    icon: 'unlock'
  },
  // T22.166 (Gordon D27 2026-10-12): a collaborator EDITED the owner's wheel --
  // one grouped notice per editing session ("Kelly edited <Wheel> (3 segments
  // changed)"), addressed to the OWNER (BE yuga_wheel/collaboration/
  // edit_notify.py). Matches notifications/models.py
  // Notifications.COLLABORATION_EDITED (=26; 24 is DATA_ENTRY_REVIEW_NEEDED). ref_id = wheel id -> wheel view.
  COLLABORATION_EDITED: {
    value: 26,
    description: 'Collaborator edited your wheel notification',
    icon: 'collab'
  }
};

/** Owner review inbox deep link (T28.7). */
export const dataEntryReviewPath = (submissionId) =>
  submissionId != null
    ? `/data-entry-reviews?submission=${encodeURIComponent(submissionId)}`
    : '/data-entry-reviews';

/**
 * Notification types whose instance is a WHEEL (backend instance_type=WHEEL),
 * i.e. their ref_id is a wheel id and they route to the wheel view.
 * Authoritative against notifications/models.py: share/clone/liveclone/favourite/
 * unlock/lock (1-6), ai_wheel (15), comment (16), wheel_reaction (17 — added
 * 2026-08-12, confirmed against notifications/migrations/0009_alter_notifications_type.py
 * + a live-measured type-17 row whose instance_type/image/instance_name matched
 * this account's confirmed wheel-instance rows for the same wheel).
 */
const WHEEL_INSTANCE_TYPES = [
  NOTIFICATION_TYPES.SHARE.value,
  NOTIFICATION_TYPES.CLONE.value,
  NOTIFICATION_TYPES.LIVE_CLONE.value,
  NOTIFICATION_TYPES.FAVORITE.value,
  NOTIFICATION_TYPES.UNLOCK.value,
  NOTIFICATION_TYPES.LOCK.value,
  NOTIFICATION_TYPES.AI_WHEEL.value,
  NOTIFICATION_TYPES.COMMENT.value,
  NOTIFICATION_TYPES.WHEEL_REACTION.value,
  // ISP-9999 (60-D): a collaboration invite's ref_id is the wheel id (BE
  // service.notify_invitee_in_app: instance_type=Notifications.WHEEL) -- joins
  // the wheel-thumbnail gate like every other wheel-instance type.
  NOTIFICATION_TYPES.COLLABORATION_INVITE.value,
  // ISP-9999 (collab-link-join-no-notification): same shape -- the join
  // notification's ref_id is also the wheel id (BE
  // notify_owner_of_link_join: instance_type=Notifications.WHEEL). No
  // extras.token deep-link needed (unlike the invite type): the owner just
  // wants to land on the wheel, so it needs no special case in
  // getNotificationRedirectUrl below, only this list entry.
  NOTIFICATION_TYPES.COLLABORATION_JOINED.value,
  // T22.166: same shape as JOINED -- ref_id is the wheel the collaborator edited.
  NOTIFICATION_TYPES.COLLABORATION_EDITED.value,
];

/**
 * Helper function to get notification type by value
 * @param {number} value - The numeric value of the notification type
 * @returns {Object} The notification type object or null if not found
 */
export const getNotificationType = (value) => {
  return Object.values(NOTIFICATION_TYPES).find(type => type.value === value) || null;
};

/**
 * Helper function to check if a notification type is wheel-related (its ref_id is
 * a wheel id). Authoritative list incl. AI wheel (15) and comment (16).
 * @param {number} value - The numeric value of the notification type
 * @returns {boolean} True if the notification type routes to a wheel
 */
export const isWheelNotification = (value) => {
  return WHEEL_INSTANCE_TYPES.includes(value);
};

/**
 * Resolve the in-app destination for a notification.
 *
 * IMPORTANT (data-loss fix, 2026-07-25): returns `null` when the type has no
 * meaningful destination (unknown/unmapped type, or a wheel/profile type whose
 * ref_id is missing). Callers MUST treat `null` as "do not navigate and do not
 * mark-read" so a click that can't route never silently buries the notification.
 * The previous default of '/settings' + mark-read is exactly the bug this fixes.
 *
 * @param {number} value - notification `type`
 * @param {string} refId - notification `ref_id`. For wheel-instance types this is
 *                         the wheel id. For a FOLLOW it is the FOLLOWED user's id —
 *                         i.e. the RECIPIENT (you), NOT the follower — so it must
 *                         NOT be used to route a follow (see below).
 * @param {string|number} [actorUserId] - the acting user's id (`actor_user_id`,
 *                         BE `from_user`). For a FOLLOW this is the FOLLOWER, the
 *                         profile the click should open.
 * @param {object|null} [extras] - notification `extras` JSON (BE
 *                         Notifications.extras). Only COLLABORATION_INVITE reads
 *                         it today (extras.token -> the accept-page deep link);
 *                         every other type ignores it, so this param is additive
 *                         and existing 3-arg callers are unaffected.
 * @returns {string|null} the destination path, or null if unroutable
 */
export const getNotificationRedirectUrl = (value, refId, actorUserId, extras) => {
  // T31: a co-edit comment notification (BE yuga_wheel/coedit_comments/notify.py,
  // extras.kind "coedit_comment") deep-links into the Edit Wheel page with the
  // Comments pane focused on the thread, not the public wheel view.
  if (extras && extras.kind === 'coedit_comment' && extras.wheel_id) {
    return `/builder/${encodeURIComponent(extras.wheel_id)}?comment=${encodeURIComponent(extras.comment_id || '')}`;
  }
  // Collaboration invite (ISP-9999 / 60-D): prefer the token-addressed accept
  // page (same route the invite EMAIL links to,
  // yuga_wheel/collaboration/service.py send_collaboration_invite_email) so
  // accept/decline is one click away; fall back to the wheel itself when the
  // row somehow carries no token (still a wheel-instance type below).
  if (value === NOTIFICATION_TYPES.COLLABORATION_INVITE.value && extras && extras.token) {
    return `/collaboration/accept?token=${encodeURIComponent(extras.token)}`;
  }

  // New chat message (stgfeat-1007-P0, Gordon 2026-10-07 decision): opens the
  // conversation the message landed in, NOT a wheel/profile -- the BE
  // (messaging/services/push.py) carries conversation_id in `extras` (the
  // same payload shape already pushed to FCM), not in `ref_id` (ref_id here
  // is the SENDER's user id, used only for the row's avatar/name). No
  // conversation_id => unroutable (null, same no-dead-click contract as
  // every other type below).
  if (value === NOTIFICATION_TYPES.DM_MESSAGE.value) {
    const conversationId = extras && extras.conversation_id;
    return conversationId != null ? `/messages/${conversationId}` : null;
  }

  // Workspace (Teams) invite (kelly-1003-P2-teams-invite-no-in-app-notification):
  // route to the SAME page the invite EMAIL links to (src/pages/InviteAccept,
  // BE workspaces/services/invite_email.py build_accept_link) so Accept/Decline
  // from the notification is the identical token flow as the email link. No
  // wheel/profile fallback -- an invite with no token is unroutable (null, same
  // no-dead-click contract as every other type below).
  if (value === NOTIFICATION_TYPES.WORKSPACE_INVITE.value) {
    return extras && extras.token ? `/invite/accept?token=${encodeURIComponent(extras.token)}` : null;
  }

  // feat-0923-broken-media-alerts (SPEC.md contract): payload {wheel_id, count}.
  // wheel_id present -> open the Edit Wheel builder with the broken-media list
  // panel open (`?brokenMedia=1`), NOT `/wheel/<id>` -- unlike every OTHER
  // wheel-instance type, the destination here is the OWNER'S builder, so this
  // is handled here rather than joining WHEEL_INSTANCE_TYPES below. wheel_id
  // absent -> the notification is for a broken AVATAR ("avatar -> profile
  // edit" per SPEC.md); there is no dedicated avatar-edit route (only the
  // legacy blue-header /profile, which is banned), so this lands on the
  // Settings > Account tab -- the nearest current-nav surface a user can
  // change their profile picture from.
  // T28.7: the owner lands on the review inbox; the filler on the wheel itself
  // (unlocked after an approve, or back at the quiz after a reject).
  if (value === NOTIFICATION_TYPES.DATA_ENTRY_REVIEW_NEEDED.value) {
    return dataEntryReviewPath(extras?.submission_id);
  }
  if (value === NOTIFICATION_TYPES.DATA_ENTRY_RESULT.value) {
    if (extras?.audience === 'filler') {
      const wheelId = extras?.wheel_id ?? refId;
      return wheelId ? `/wheel/${wheelId}` : null;
    }
    return dataEntryReviewPath(extras?.submission_id);
  }

  if (value === NOTIFICATION_TYPES.BROKEN_MEDIA.value) {
    // Flag OFF -> null (no navigation, no mark-read): the builder's own
    // indicator/panel is flag-gated off too, so landing there would show
    // nothing to act on -- same no-dead-click contract as the moderation
    // case above.
    if (!BROKEN_MEDIA_ALERTS_ENABLED) return null;
    return refId ? `/builder/${refId}?brokenMedia=1` : '/settings?tab=account';
  }

  // Wheel-instance notifications -> the wheel view. No wheel id => unroutable.
  if (isWheelNotification(value)) {
    if (!refId) return null;
    // COMMENT anchor (G-161, 2026-08-12): the notification payload was READ live
    // on web-stg (GET /api/v1/notification/) across all 19 COMMENT rows on the
    // account — every row's `extras` is null and no field on the object carries a
    // comment id (only `ref_id`=wheel id). Per-comment anchoring is therefore NOT
    // possible from current data (BE gap — see PR body for the follow-up ask).
    // Best available fix: land on the wheel's COMMENTS SECTION (not just the
    // wheel page) via a `#comments` hash CommentSection reads on mount to scroll
    // + briefly highlight itself, so "comment on a wheel -> take you to the
    // comments" is at least section-accurate even though it can't be row-accurate
    // yet. Every OTHER wheel-instance type keeps its byte-identical `/wheel/<id>`.
    if (value === NOTIFICATION_TYPES.COMMENT.value) {
      return `/wheel/${refId}#comments`;
    }
    return `/wheel/${refId}`;
  }

  switch (value) {
    // Follow (routing bug fixed 2026-07-25, measured on live web-stg + BE source):
    // the follow's `ref_id` is the FOLLOWED user id — the RECIPIENT (you). BE emits
    // `ref_id=user_to_follow.id, sender=request.user.id` (home/api/v1/viewsets.py
    // follow action), so routing on ref_id sent you to your OWN profile. Route to
    // the FOLLOWER instead: `actor_user_id` (BE from_user). Current-nav gallery
    // route, NOT the legacy blue-header /profile. No actor id => unroutable (do NOT
    // fall back to ref_id, which would re-introduce the self-profile bug).
    case NOTIFICATION_TYPES.FOLLOW.value:
      return actorUserId != null ? `/gallery/${actorUserId}` : null;
    case NOTIFICATION_TYPES.TIP.value:
      return '/referrals';
    // Billing-class notices (warning / payment / expiration) -> subscriptions.
    // ISP-9999 (daniyal-0914-dev-subscription-notification-wrong-settings-url):
    // was `?option=subscriptions` — the Settings page (src/pages/Settings/index.jsx)
    // only ever reads `?tab=`, so `option=` silently landed on the default Account
    // tab. `tab=<key>` is the contract every other in-app deep link into Settings
    // uses (see Settings#selectIndex building `/settings?tab=${index}` itself).
    case NOTIFICATION_TYPES.WARNING.value:
    case NOTIFICATION_TYPES.AUTO_DEPOSIT.value: // BE value 11 = Payment
    case NOTIFICATION_TYPES.STORAGE_WARNING.value: // BE value 10 = Expiration
      return '/settings?tab=subscriptions';
    case NOTIFICATION_TYPES.TAX.value:
      return '/settings?tab=security';
    // Moderation content-hidden (copy-0920, was cm-0919b): only route into the
    // Content Moderation tab when it's actually registered
    // (MODERATION_APPEALS_ENABLED -- cm-0919 split this off
    // MODERATION_CONSOLE_ENABLED, the unrelated staff-only console flag;
    // src/pages/Settings/index.jsx gates that tab on this same flag today).
    // Flag OFF -> null (no navigation at all) -- there is no more in-notification
    // fallback text to show instead (copy-0920 removed both the appeal and the
    // contact-support endings), so this stays the existing no-dead-click
    // contract: a click on a row that can't route does nothing.
    // Carries the flag id (extras.content_flag_id, present on every row created
    // by owner_notify.py) so the target page can scroll to + highlight the
    // specific flagged item; falls back to the bare tab route for any row that
    // predates that payload field.
    case NOTIFICATION_TYPES.MODERATION_CONTENT_HIDDEN.value: {
      if (!MODERATION_APPEALS_ENABLED) return null;
      const flagId = extras?.content_flag_id;
      return flagId != null
        ? `/settings?tab=contentModeration&flagId=${encodeURIComponent(flagId)}`
        : '/settings?tab=contentModeration';
    }
    // Storage-capacity notices -> subscriptions (plan/storage management).
    case NOTIFICATION_TYPES.RED_STORAGE.value: // BE 13 = Storage Capacity Full
    case NOTIFICATION_TYPES.YELLOW_STORAGE.value: // BE 14 = Storage Capacity Warning
      return '/settings?tab=subscriptions';
    default:
      // Unknown/unmapped type -> DON'T bury the row: no navigation, no mark-read.
      return null;
  }
};